import React, { useState, useRef } from 'react';
import { Printer, FileDown, Mail, MessageCircle, X, Share2, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_colabella-tutorials/artifacts/wcwuz41r_LOGO.png';

const SectionActions = ({ sectionTitle, contentRef }) => {
  const [showShare, setShowShare] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const handlePrint = () => {
    window.print();
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const element = contentRef?.current || document.querySelector('.page-container');
      if (!element) {
        setGenerating(false);
        return;
      }

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        scrollY: -window.scrollY,
        windowHeight: element.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const pdf = new jsPDF({
        orientation: imgWidth > imgHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Add header with logo and company name
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Colabella Group — ' + sectionTitle, 10, 8);

      const date = new Date().toLocaleDateString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
      pdf.text(date, pdfWidth - 10, 8, { align: 'right' });

      // Calculate dimensions to fit content
      const margin = 10;
      const topMargin = 12;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - topMargin - margin;

      const ratio = Math.min(
        availableWidth / (imgWidth / (canvas.width / availableWidth)),
        1
      );

      const scaledWidth = availableWidth;
      const scaledHeight = (imgHeight / imgWidth) * scaledWidth;

      // If content fits in one page
      if (scaledHeight <= availableHeight) {
        pdf.addImage(imgData, 'JPEG', margin, topMargin, scaledWidth, scaledHeight);
      } else {
        // Multi-page: split content
        let yOffset = 0;
        let pageNum = 0;
        const pageContentHeight = availableHeight;
        const totalPages = Math.ceil(scaledHeight / pageContentHeight);

        while (yOffset < scaledHeight) {
          if (pageNum > 0) {
            pdf.addPage();
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);
            pdf.text('Colabella Group — ' + sectionTitle + ' (pag. ' + (pageNum + 1) + '/' + totalPages + ')', 10, 8);
            pdf.text(date, pdfWidth - 10, 8, { align: 'right' });
          }

          // Create a cropped version for this page
          const sourceY = (yOffset / scaledHeight) * imgHeight;
          const sourceHeight = Math.min(
            (pageContentHeight / scaledHeight) * imgHeight,
            imgHeight - sourceY
          );
          const destHeight = Math.min(pageContentHeight, scaledHeight - yOffset);

          // Use canvas cropping
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = imgWidth;
          pageCanvas.height = sourceHeight;
          const ctx = pageCanvas.getContext('2d');
          ctx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);

          const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.85);
          pdf.addImage(pageImgData, 'JPEG', margin, topMargin, scaledWidth, destHeight);

          yOffset += pageContentHeight;
          pageNum++;
        }
      }

      // Footer
      const totalPagesCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPagesCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(180, 180, 180);
        pdf.text('Generato da Colabella Group App', margin, pdfHeight - 5);
      }

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfUrl(url);
      setShowShare(true);
    } catch (e) {
      console.error('PDF generation error:', e);
      alert('Errore nella generazione del PDF. Riprova.');
    }
    setGenerating(false);
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Colabella_Group_${sectionTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Colabella Group — Riepilogo ${sectionTitle}`);
    const body = encodeURIComponent(
      `Buongiorno,\n\nIn allegato il riepilogo della sezione "${sectionTitle}" di Colabella Group.\n\nData: ${new Date().toLocaleDateString('it-IT')}\n\nCordiali saluti,\nColabella Group`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    // Also trigger download so user can attach it
    handleDownload();
  };

  const handleWhatsApp = () => {
    const text = encodeURIComponent(
      `*Colabella Group*\nRiepilogo: ${sectionTitle}\nData: ${new Date().toLocaleDateString('it-IT')}\n\nScarica il PDF dal link dell'app.`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
    // Also trigger download
    handleDownload();
  };

  const handleNativeShare = async () => {
    if (navigator.share && pdfBlob) {
      try {
        const file = new File(
          [pdfBlob],
          `Colabella_Group_${sectionTitle.replace(/\s+/g, '_')}.pdf`,
          { type: 'application/pdf' }
        );
        await navigator.share({
          title: `Colabella Group — ${sectionTitle}`,
          text: `Riepilogo ${sectionTitle}`,
          files: [file],
        });
      } catch (e) {
        // Fallback to download
        handleDownload();
      }
    } else {
      handleDownload();
    }
  };

  return (
    <>
      <div className="section-action-buttons">
        <button className="section-action-btn print-btn" onClick={handlePrint} title="Stampa sezione">
          <Printer size={16} />
          <span>Stampa</span>
        </button>
        <button
          className="section-action-btn pdf-btn"
          onClick={generatePDF}
          disabled={generating}
          title="Crea PDF e condividi"
        >
          {generating ? (
            <><span className="btn-spinner" /> Generazione...</>
          ) : (
            <><FileDown size={16} /><span>PDF e Condividi</span></>
          )}
        </button>
      </div>

      {/* Share Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Condividi Riepilogo — {sectionTitle}</DialogTitle>
          </DialogHeader>
          <p className="share-subtitle">
            Il PDF è pronto. Scegli come condividerlo:
          </p>
          <div className="share-options">
            <button className="share-option-btn share-download" onClick={handleDownload}>
              <Download size={22} />
              <span>Scarica PDF</span>
            </button>
            <button className="share-option-btn share-email" onClick={handleEmail}>
              <Mail size={22} />
              <span>Invia via Email</span>
            </button>
            <button className="share-option-btn share-whatsapp" onClick={handleWhatsApp}>
              <MessageCircle size={22} />
              <span>Invia su WhatsApp</span>
            </button>
            {navigator.share && (
              <button className="share-option-btn share-native" onClick={handleNativeShare}>
                <Share2 size={22} />
                <span>Condividi...</span>
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SectionActions;
